import type Hls from 'hls.js';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { type ShortIdProviderProps } from '@air/provider-short-id';
import { reportErrorToBugsnag } from '@air/utils-error';
import { cn } from '@air/utils-tailwind';
import { isHlsSupported, loadHls } from '@air/utils-video';

type PresentationBlock = NonNullable<ShortIdProviderProps['presentationContent']>['blocks'][number];

/*
 * Absent-value defaults for the wire's optional playback toggles. The wire stores only explicit
 * author choices; the frontend owns these fallbacks, so they MUST match what the authoring
 * surface shows as the initial toggle state or the editor would display a selection that differs
 * from what the public page actually plays.
 */
const VIDEO_AUTOPLAY_DEFAULT = true;
const VIDEO_LOOP_DEFAULT = true;
const VIDEO_MUTED_DEFAULT = true;
const VIDEO_CONTROLS_DEFAULT = false;

export type PublicShowcaseVideoBlockProps = {
  block: PresentationBlock;
};

export const PublicShowcaseVideoBlock = memo(({ block }: PublicShowcaseVideoBlockProps) => {
  const hlsRef = useRef<Hls | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  /*
   * A <video> with no source yet reports the spec's 300x150 default, so `w-full h-auto` collapses
   * the block to a ~150px strip and then jumps to the real height once metadata arrives. That jump
   * lands AFTER everything below it mounted — including the virtualized listing grid, whose scroll
   * math is measured against a position that just moved (FOUND-756). The wire carries no intrinsic
   * dimensions on video blocks, so hold a plausible box until the element knows its own and
   * release it as soon as it does.
   */
  const [hasIntrinsicSize, setHasIntrinsicSize] = useState(false);

  const handleLoadedMetadata = useCallback(() => setHasIntrinsicSize(true), []);

  const url = block.url;
  const isHlsStream = !!url && url.includes('.m3u8');

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !url) {
      return;
    }

    let cancelled = false;

    const loadVideo = async () => {
      try {
        if (isHlsStream && isHlsSupported()) {
          hlsRef.current = await loadHls({ src: url, videoElement: video });
          return;
        }

        // Direct file urls, and HLS on browsers without MSE (Safari plays .m3u8 natively).
        video.src = url;
      } catch (error) {
        if (!cancelled) {
          reportErrorToBugsnag({
            error,
            context: 'Failed to load HLS video in PublicShowcaseVideoBlock',
            metadata: {
              key: 'Data',
              data: { src: url },
            },
          });
        }
      }
    };

    void loadVideo();

    return () => {
      cancelled = true;
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [isHlsStream, url]);

  // The wire guarantees a resolved url on video blocks — an absent url is envelope skew and
  // renders nothing rather than an empty player.
  if (!url) {
    return null;
  }

  const autoplay = block.autoplay ?? VIDEO_AUTOPLAY_DEFAULT;
  // Browsers block unmuted autoplay outright — when autoplay is on, muted must win over a stored
  // muted: false or the video would never start. The controls (when shown) still let the visitor
  // unmute manually.
  const muted = autoplay ? true : (block.muted ?? VIDEO_MUTED_DEFAULT);
  const loop = block.loop ?? VIDEO_LOOP_DEFAULT;
  const controls = block.controls ?? VIDEO_CONTROLS_DEFAULT;

  const extendToEdges = !!block.extendToEdges;
  // Absent roundCorners renders rounded (the authoring default); full-bleed media is always
  // square-edged — a radius on an edge-to-edge video would punch holes into the column edges.
  const hasRoundedCorners = !extendToEdges && block.roundCorners !== false;

  return (
    <div
      // Inset blocks share the links block's px-4 py-3 gutter; extendToEdges renders flush to the
      // content column's edges (the column itself stays width-constrained — same contract as the
      // cover's `fullWidth` variant, which bleeds to the column, not the viewport).
      className={cn('w-full', !extendToEdges && 'px-4 py-3')}
      data-block-id={block.id}
      data-testid="PUBLIC_COLLECTION_VIDEO_BLOCK"
    >
      {/* The source is attached by the effect above (hls.js or direct src), so the native
          autoplay/loop/muted attributes take effect once media loads. preload only matters on the
          non-autoplay path — metadata keeps a paused, controls-driven block from downloading the
          whole file up front. */}
      <video
        ref={videoRef}
        autoPlay={autoplay}
        controls={controls}
        loop={loop}
        muted={muted}
        playsInline
        preload={autoplay ? 'auto' : 'metadata'}
        onLoadedMetadata={handleLoadedMetadata}
        data-testid="PUBLIC_COLLECTION_VIDEO_BLOCK_VIDEO"
        // The radius var follows the link's Corner rounding setting (set on the page root by
        // PublicShowcasePage); the 12px fallback matches the links card's outer radius.
        className={cn(
          'block h-auto w-full',
          hasRoundedCorners && 'rounded-[var(--gallery-card-radius,12px)]',
          // The reserved box above: a 16/9 placeholder tinted with a scale color, so it flips with
          // the page theme on its own. object-contain keeps a frame that arrives before metadata
          // from stretching inside it. Both drop away once the element reports its own dimensions,
          // handing the height back to the video's ratio — identical to before this box existed.
          !hasIntrinsicSize && 'aspect-video bg-gray-3 object-contain',
        )}
      />
    </div>
  );
});

PublicShowcaseVideoBlock.displayName = 'PublicShowcaseVideoBlock';
